import React, { useState, useEffect } from 'react';
import {
	Box,
	Button,
	Container,
	FormControl,
	FormLabel,
	Input,
	Stack,
	Heading,
	Text,
	useColorModeValue,
	Tabs,
	TabList,
	TabPanels,
	Tab,
	TabPanel,
	InputGroup,
	InputLeftElement,
	Icon,
	Divider,
	Center
} from '@chakra-ui/react';
import { FaUser, FaEnvelope, FaLock, FaPhone, FaGoogle } from 'react-icons/fa';
import { useUser } from '../context/UserContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { api_url } from '../url';

const Login = () => {
	const [loginType, setLoginType] = useState('email');
	const [identifier, setIdentifier] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [isRedirecting, setIsRedirecting] = useState(false);
	const { login } = useUser();
	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		// Handle token from Google OAuth redirect
		const query = new URLSearchParams(location.search);
		const token = query.get('token');
		const errorParam = query.get('error');

		if (token) {
			localStorage.setItem('token', token);
			setSuccess("Google login successful! Redirecting...");
			setIsRedirecting(true);
			setTimeout(() => {
				navigate('/');
			}, 1500);
		}

		if (errorParam) {
			if (errorParam === 'unauthorized-access') {
				setError("Access denied: You must have an Admin or Super Admin account to access this dashboard.");
			} else {
				setError("Google login failed. Please try again.");
			}
		}
	}, [location, navigate]);

	const handleTabChange = (index) => {
		if (index === 0) setLoginType('email');
		else if (index === 1) setLoginType('username');
		else setLoginType('mobile');
		setIdentifier('');
		setError(null);
		setSuccess(null);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError(null);
		setSuccess(null);

		if (!identifier || !password) {
			setError("Please fill in all fields");
			return;
		}

		try {
			await login({
				[loginType]: identifier,
				password
			});

			setSuccess("Login successful! Redirecting to dashboard...");
			setIsRedirecting(true);

			setTimeout(() => {
				navigate('/');
			}, 2000);
		} catch (error) {
			setError(error.message || "An error occurred during login");
			setIsRedirecting(false);
		}
	};

	const handleGoogleLogin = () => {
		window.location.href = `${api_url}/users/auth/google/dashboard`;
	};

	return (
		<Container maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', md: '8' }}>
			<Stack spacing="8">
				<Box
					py={{ base: '0', sm: '8' }}
					px={{ base: '4', sm: '10' }}
					bg={useColorModeValue('white', 'gray.700')}
					boxShadow={{ base: 'none', sm: 'xl' }}
					borderRadius={{ base: 'none', sm: 'xl' }}
				>
					<Stack spacing="6">
						<Stack spacing={{ base: '2', md: '3' }} textAlign="center">
							<Heading size={{ base: 'xs', md: 'lg' }} color="blue.500">
								Welcome Back
							</Heading>
							<Text color="gray.500">
								Sign in to continue JSK Book dashboard
							</Text>
						</Stack>

						<Tabs isFitted variant='enclosed' onChange={handleTabChange} isDisabled={isRedirecting}>
							<TabList mb='1em'>
								<Tab _selected={{ color: 'white', bg: 'blue.500' }}>Email</Tab>
								<Tab _selected={{ color: 'white', bg: 'blue.500' }}>Username</Tab>
								<Tab _selected={{ color: 'white', bg: 'blue.500' }}>Mobile</Tab>
							</TabList>
							<TabPanels>
								<TabPanel p={0} />
								<TabPanel p={0} />
								<TabPanel p={0} />
							</TabPanels>
						</Tabs>

						<form onSubmit={handleSubmit}>
							<Stack spacing="5">
								<FormControl isRequired>
									<FormLabel textTransform="capitalize">{loginType}</FormLabel>
									<InputGroup>
										<InputLeftElement pointerEvents='none'>
											<Icon
												as={
													loginType === 'email' ? FaEnvelope :
														loginType === 'mobile' ? FaPhone : FaUser
												}
												color='gray.300'
											/>
										</InputLeftElement>
										<Input
											type={loginType === 'email' ? 'email' : 'text'}
											value={identifier}
											onChange={(e) => setIdentifier(e.target.value)}
											placeholder={`Enter your ${loginType}`}
										/>
									</InputGroup>
								</FormControl>

								<FormControl isRequired isDisabled={isRedirecting}>
									<FormLabel>Password</FormLabel>
									<InputGroup>
										<InputLeftElement pointerEvents='none'>
											<Icon as={FaLock} color='gray.300' />
										</InputLeftElement>
										<Input
											type="password"
											value={password}
											onChange={(e) => setPassword(e.target.value)}
											placeholder="Enter your password"
										/>
									</InputGroup>
								</FormControl>

								{error && (
									<Box bg="red.100" color="red.700" p={3} borderRadius="md" fontSize="sm">
										{error}
									</Box>
								)}
								{success && (
									<Box bg="green.100" color="green.700" p={3} borderRadius="md" fontSize="sm">
										{success}
									</Box>
								)}

								<Button
									type="submit"
									colorScheme="blue"
									size="lg"
									fontSize="md"
									boxShadow="md"
									isLoading={isRedirecting}
									loadingText="Redirecting..."
									_hover={{
										transform: 'translateY(-2px)',
										boxShadow: 'lg',
									}}
								>
									Sign in
								</Button>

								<Box position="relative" py="4">
									<Divider />
									<Center
										position="absolute"
										top="50%"
										left="50%"
										transform="translate(-50%, -50%)"
										bg={useColorModeValue('white', 'gray.700')}
										px="2"
									>
										<Text fontSize="sm" color="gray.500">
											OR
										</Text>
									</Center>
								</Box>

								<Button
									variant="outline"
									leftIcon={<FaGoogle color="#EA4335" />}
									onClick={handleGoogleLogin}
									isDisabled={isRedirecting}
									size="lg"
									fontSize="md"
									_hover={{
										bg: 'gray.50',
										transform: 'translateY(-1px)',
									}}
								>
									Continue with Google
								</Button>
							</Stack>
						</form>
					</Stack>
				</Box>
			</Stack>
		</Container>
	);
};

export default Login;
